import type { ReadonlyJSONValue } from "@rocicorp/zero";
import {
  type ChatTool,
  toolResultFailed,
  type ToolUseProps,
} from "../components/Chat/ChatTool";
import { Step } from "../components/Step";
import { Text } from "../components/Text";
import type { BashData } from "./Bash";

function parseInput(toolUse: ToolUseProps["toolUse"]) {
  const v = toolUse.input;
  return v && typeof v === "object" && !Array.isArray(v)
    ? (v as Record<string, ReadonlyJSONValue>)
    : null;
}

function str(v: ReadonlyJSONValue | undefined): string | undefined {
  return typeof v === "string" ? v : undefined;
}

function stepFromInput(
  input: ReturnType<typeof parseInput>
): string | undefined {
  const explicit = str(input?.step);
  if (explicit) return explicit;
  const path = str(input?.path);
  if (!path) return undefined;
  const first = path.replace(/^\/+/, "").split("/")[0];
  return first && !first.startsWith(".") ? first : undefined;
}

function EditFileHeader({ toolUse, result, data, interrupted }: ToolUseProps) {
  const input = parseInput(toolUse);
  const stepName = stepFromInput(input);
  const color = stepName
    ? (data as BashData | undefined)?.getStepColor(stepName)
    : undefined;
  const fileName = str(input?.path)?.split("/").pop();

  return (
    <Step
      color={color ? `var(--${color}-600)` : undefined}
      icon="square-pen"
      name={stepName}
      done={!!result}
      interrupted={interrupted}
      pastParticiple={str(input?.completedDescription) ?? "Edited"}
      failure={
        result && toolResultFailed(result)
          ? (str(input?.failedDescription) ?? "Failed")
          : undefined
      }
      inProgress={
        str(input?.activeDescription)
          ? `${str(input?.activeDescription)}…`
          : fileName
            ? `Editing ${fileName}…`
            : "Editing…"
      }
    />
  );
}

function EditFileBody({ toolUse, result }: ToolUseProps) {
  const input = parseInput(toolUse);
  const path = str(input?.path);
  const oldString = str(input?.oldString);
  const newString = str(input?.newString);

  return (
    <Text variant="code-small" color="var(--text-secondary)">
      {path && (
        <pre
          style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {path}
        </pre>
      )}
      {oldString !== undefined && (
        <pre
          style={{
            margin: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            color: "var(--red-600)",
          }}
        >
          {`- ${oldString}`}
        </pre>
      )}
      {newString !== undefined && (
        <pre
          style={{
            margin: 0,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            color: "var(--green-600)",
          }}
        >
          {`+ ${newString}`}
        </pre>
      )}
      {result && (
        <pre
          style={{ margin: 0, whiteSpace: "pre-wrap", wordBreak: "break-word" }}
        >
          {JSON.stringify(result.output, null, 2)}
        </pre>
      )}
    </Text>
  );
}

export const editFile = (data: BashData): ChatTool => ({
  name: "editFile",
  Header: EditFileHeader,
  Body: EditFileBody,
  data,
});
